import React, { useEffect, useRef, useState } from 'react';
import {
    createInitialSnapshot,
    handlePointerShot,
    HuntGameSnapshot,
    resetBirdIds,
    spawnBird,
    updateGame,
} from './huntEngine';
import {
    drawBirdSprite,
    drawCrosshair,
    drawFeathers,
    drawHuntBackground,
    FeatherParticle,
    HuntBackgroundImages,
    spawnFeathers,
    updateFeathers,
} from './huntDraw';
import {
    drawScorePopups,
    ScorePopup,
    spawnMissPopup,
    spawnScorePopup,
    updateScorePopups,
} from './huntScorePopups';
import { HUNT_BG, HUNT_SPRITE_SHEETS, HUNT_UI } from './huntConfig';
import { drawSpriteFrame } from './huntSprites';

export type HuntCanvasVariant = 'preview' | 'game';

interface HuntCanvasProps {
    variant: HuntCanvasVariant;
    playing: boolean;
    sessionKey: number;
    reducedMotion: boolean;
    onSnapshot?: (snapshot: HuntGameSnapshot) => void;
    onFinished?: (snapshot: HuntGameSnapshot) => void;
}

interface PreviewBird {
    x: number;
    y: number;
    vx: number;
    width: number;
    height: number;
    direction: 1 | -1;
    sheetKey: 'normalFlap' | 'bonusFlap';
    frame: number;
    frameTimer: number;
}

const HuntCanvas: React.FC<HuntCanvasProps> = ({
    variant,
    playing,
    sessionKey,
    reducedMotion,
    onSnapshot,
    onFinished,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const snapshotRef = useRef<HuntGameSnapshot>(createInitialSnapshot());
    const spawnTimerRef = useRef(400);
    const elapsedRef = useRef(0);
    const imagesRef = useRef<Record<string, HTMLImageElement>>({});
    const feathersRef = useRef<FeatherParticle[]>([]);
    const scorePopupsRef = useRef<ScorePopup[]>([]);
    const previewBirdsRef = useRef<PreviewBird[]>([]);
    const rafRef = useRef<number | null>(null);
    const lastTsRef = useRef<number | null>(null);
    const finishedRef = useRef(false);
    const lastHudPushRef = useRef(0);
    const onSnapshotRef = useRef(onSnapshot);
    const onFinishedRef = useRef(onFinished);
    const [imagesReady, setImagesReady] = useState(false);

    onSnapshotRef.current = onSnapshot;
    onFinishedRef.current = onFinished;

    useEffect(() => {
        const loadImage = (src: string) =>
            new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = src;
            });

        const sheetLoads = Object.entries(HUNT_SPRITE_SHEETS).map(([key, def]) =>
            loadImage(def.src).then((img) => [key, img] as const)
        );
        const bgLoads = [
            loadImage(HUNT_BG.sky).then((img) => ['sky', img] as const),
            loadImage(HUNT_UI.crosshair).then((img) => ['crosshair', img] as const),
        ];

        Promise.all([...sheetLoads, ...bgLoads])
            .then((entries) => {
                imagesRef.current = Object.fromEntries(entries);
                setImagesReady(true);
            })
            .catch((err) => {
                console.error('Failed to load hunt assets', err);
            });
    }, []);

    useEffect(() => {
        if (variant !== 'game') {
            return;
        }
        resetBirdIds();
        snapshotRef.current = createInitialSnapshot();
        spawnTimerRef.current = 400;
        elapsedRef.current = 0;
        finishedRef.current = false;
        lastTsRef.current = null;
        lastHudPushRef.current = 0;
        feathersRef.current = [];
        scorePopupsRef.current = [];
    }, [sessionKey, variant]);

    const getBackgroundImages = (): HuntBackgroundImages => ({
        sky: imagesRef.current.sky,
    });

    const pushSnapshotThrottled = (ts: number) => {
        if (ts - lastHudPushRef.current < 80) {
            return;
        }
        lastHudPushRef.current = ts;
        onSnapshotRef.current?.({ ...snapshotRef.current, birds: [...snapshotRef.current.birds] });
    };

    useEffect(() => {
        const shouldAnimate = variant === 'preview' || (variant === 'game' && playing);
        if (!shouldAnimate || !imagesReady) {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            return;
        }

        const canvas = canvasRef.current;
        const wrapper = wrapperRef.current;
        if (!canvas || !wrapper) {
            return;
        }

        const resize = () => {
            const rect = wrapper.getBoundingClientRect();
            if (rect.width < 1 || rect.height < 1) {
                return;
            }
            const dpr = window.devicePixelRatio || 1;
            const nextW = Math.floor(rect.width * dpr);
            const nextH = Math.floor(rect.height * dpr);
            // Reassigning canvas.width clears + reallocs the buffer; only do it on real size changes.
            if (canvas.width === nextW && canvas.height === nextH) {
                return;
            }
            canvas.width = nextW;
            canvas.height = nextH;
            canvas.style.width = `${rect.width}px`;
            canvas.style.height = `${rect.height}px`;
        };

        resize();
        window.addEventListener('resize', resize);

        if (variant === 'preview' && previewBirdsRef.current.length === 0) {
            previewBirdsRef.current = [
                { x: -80, y: 0, vx: 1.1, width: 96, height: 96, direction: 1, sheetKey: 'normalFlap', frame: 0, frameTimer: 0 },
                { x: 200, y: 0, vx: -0.9, width: 96, height: 96, direction: -1, sheetKey: 'normalFlap', frame: 1, frameTimer: 0 },
                { x: -120, y: 0, vx: 1.4, width: 128, height: 128, direction: 1, sheetKey: 'bonusFlap', frame: 0, frameTimer: 0 },
            ];
        }

        const loop = (ts: number) => {
            if (lastTsRef.current === null) {
                lastTsRef.current = ts;
            }
            const deltaMs = Math.min(50, ts - lastTsRef.current);
            lastTsRef.current = ts;

            resize();

            const dpr = window.devicePixelRatio || 1;
            const width = canvas.width;
            const height = canvas.height;
            const ctx = canvas.getContext('2d');
            if (!ctx || width < 1 || height < 1) {
                rafRef.current = requestAnimationFrame(loop);
                return;
            }

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);
            const cssWidth = width / dpr;
            const cssHeight = height / dpr;

            drawHuntBackground(ctx, cssWidth, cssHeight, ts, getBackgroundImages(), reducedMotion);

            if (variant === 'preview') {
                previewBirdsRef.current.forEach((bird, i) => {
                    const baseY = cssHeight * (0.22 + i * 0.18);
                    bird.x += bird.vx;
                    if (bird.direction === 1 && bird.x > cssWidth + bird.width) {
                        bird.x = -bird.width;
                    }
                    if (bird.direction === -1 && bird.x < -bird.width) {
                        bird.x = cssWidth + bird.width;
                    }
                    if (!reducedMotion) {
                        bird.frameTimer += deltaMs;
                        if (bird.frameTimer > 120) {
                            const sheet = HUNT_SPRITE_SHEETS[bird.sheetKey];
                            bird.frame = (bird.frame + 1) % sheet.frameCount;
                            bird.frameTimer = 0;
                        }
                    }
                    const img = imagesRef.current[bird.sheetKey];
                    const sheet = HUNT_SPRITE_SHEETS[bird.sheetKey];
                    if (img) {
                        ctx.save();
                        ctx.translate(bird.x + bird.width / 2, baseY);
                        ctx.scale(bird.direction, 1);
                        drawSpriteFrame(
                            ctx,
                            img,
                            sheet,
                            bird.frame,
                            -bird.width / 2,
                            -bird.height / 2,
                            bird.width,
                            bird.height
                        );
                        ctx.restore();
                    }
                });
                rafRef.current = requestAnimationFrame(loop);
                return;
            }

            elapsedRef.current += deltaMs;
            feathersRef.current = updateFeathers(feathersRef.current, deltaMs);
            scorePopupsRef.current = updateScorePopups(scorePopupsRef.current, deltaMs);

            const update = updateGame(
                snapshotRef.current,
                deltaMs,
                ts,
                cssWidth,
                cssHeight,
                spawnTimerRef.current,
                elapsedRef.current
            );
            snapshotRef.current = update.snapshot;
            spawnTimerRef.current = update.spawnTimerMs;

            if (update.shouldSpawn) {
                const bird = spawnBird(snapshotRef.current, cssWidth, cssHeight, elapsedRef.current);
                if (bird) {
                    snapshotRef.current.birds.push(bird);
                }
            }

            if (ts < snapshotRef.current.flashUntil) {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
                ctx.fillRect(0, 0, cssWidth, cssHeight);
            }

            snapshotRef.current.birds.forEach((bird) => {
                drawBirdSprite(ctx, bird, imagesRef.current, ts, reducedMotion);
            });
            drawFeathers(ctx, feathersRef.current, imagesRef.current.hitFeathers);
            drawScorePopups(ctx, scorePopupsRef.current);
            drawCrosshair(ctx, snapshotRef.current, imagesRef.current.crosshair);

            pushSnapshotThrottled(ts);

            if (snapshotRef.current.finished && !finishedRef.current) {
                finishedRef.current = true;
                onSnapshotRef.current?.({ ...snapshotRef.current, birds: [...snapshotRef.current.birds] });
                onFinishedRef.current?.(snapshotRef.current);
            } else {
                rafRef.current = requestAnimationFrame(loop);
            }
        };

        rafRef.current = requestAnimationFrame(loop);

        return () => {
            window.removeEventListener('resize', resize);
            if (variant === 'preview') {
                previewBirdsRef.current = [];
            }
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            lastTsRef.current = null;
        };
    }, [variant, playing, sessionKey, reducedMotion, imagesReady]);

    useEffect(() => {
        if (variant !== 'game' || playing || !imagesReady) {
            return;
        }
        const canvas = canvasRef.current;
        const wrapper = wrapperRef.current;
        if (!canvas || !wrapper) {
            return;
        }
        const rect = wrapper.getBoundingClientRect();
        if (rect.width < 1) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        const dpr = window.devicePixelRatio || 1;
        const cssWidth = canvas.width / dpr;
        const cssHeight = canvas.height / dpr;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        drawHuntBackground(ctx, cssWidth, cssHeight, performance.now(), getBackgroundImages(), reducedMotion);
        snapshotRef.current.birds.forEach((bird) => {
            drawBirdSprite(ctx, bird, imagesRef.current, performance.now(), reducedMotion);
        });
        drawFeathers(ctx, feathersRef.current, imagesRef.current.hitFeathers);
    }, [variant, playing, imagesReady, reducedMotion, sessionKey]);

    const handlePointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
        if (variant !== 'game' || !playing || snapshotRef.current.finished) {
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const hit = handlePointerShot(snapshotRef.current, x, y, performance.now(), rect.width);
        if (hit) {
            feathersRef.current.push(...spawnFeathers(x, y));
            const basePoints = hit.points - hit.comboBonus;
            scorePopupsRef.current.push(
                spawnScorePopup(x, y - 12, basePoints, hit.comboBonus, hit.comboMilestone)
            );
            onSnapshotRef.current?.({ ...snapshotRef.current, birds: [...snapshotRef.current.birds] });
        } else {
            scorePopupsRef.current.push(spawnMissPopup(x, y - 12));
        }
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
        if (variant !== 'game' || !playing) {
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const rect = canvas.getBoundingClientRect();
        snapshotRef.current.pointerX = event.clientX - rect.left;
        snapshotRef.current.pointerY = event.clientY - rect.top;
    };

    return (
        <div ref={wrapperRef} className="HuntCanvas-wrapper">
            <canvas
                ref={canvasRef}
                className="HuntCanvas"
                onPointerDown={handlePointer}
                onPointerMove={handlePointerMove}
            />
        </div>
    );
};

export default HuntCanvas;
